import React, { useContext } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import moment from 'moment'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import ExperienceOfferPageTicketSelection from '../ExperienceOfferPage/ExperienceOfferPageTicketSelection'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'components/Luxkit/Typography/Caption'
import Modal from 'components/Luxkit/Modal/Modal'
import TextLink from 'components/Luxkit/TextLink'
import ExperienceOfferPageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'

interface Props {
  experienceView: App.ExperienceBookingView;
}

function ExperienceGiveAsGiftModal(props: Props) {
  const {
    experienceView,
  } = props

  const { redemptionLocation, pickupPoint } = useContext(ExperienceOfferPageStateContext)
  const availableTimesState = useExperienceAvailableTimes(experienceView.originalExperience, {
    isGift: true,
    redemptionLocationId: redemptionLocation?.id,
    pickupPointId: pickupPoint?.id,
  })
  const ticketOptions = useExperienceTicketsOptions(availableTimesState?.slots, { isGift: true })

  const hasExpirationDates = !!(experienceView.originalExperience.bookByDate || experienceView.originalExperience.expirationDate)
  const showExpirationMessage = hasExpirationDates && !ticketOptions.some(ticket => ticket.bookByDate)

  return (
    <Modal
      title="Give this experience as a gift"
      size="L"
    >
      {experienceView.originalExperience.giftType === 'offer' && <VerticalSpacer gap={8}>
        <BodyText variant="large">
          Purchasing this as a gift will secure the offer for your recipient and give them the flexibility to choose their dates at a later time.
          Dates must be within the book by and validity period and are subject to availability and surcharges.
        </BodyText>
        <BodyText variant="large">
          Your recipient can manage and make changes to their booking according to our 7-day ‘change of mind’ guarantee after booking.
        </BodyText>
        {showExpirationMessage && <BodyText variant="large">
          Your recipient must book by {moment(experienceView.originalExperience?.bookByDate ?? experienceView?.originalExperience?.expirationDate).format(DMY_CASUAL_FORMAT)}{' '}
          for a date up to {moment(experienceView?.originalExperience?.expirationDate).format(DMY_CASUAL_FORMAT)}.*
        </BodyText>}
        <Caption variant="medium">*Subject to availability and seasonal surcharges.</Caption>
      </VerticalSpacer>
    }
      {experienceView.originalExperience.giftType === 'gift-card' && <VerticalSpacer gap={8}>
        <BodyText variant="large">
          Purchasing this will send a gift card to your recipient and give them the flexibility to book the experience at a later time.
        </BodyText>
        <BodyText variant="large">
          Please note Gift Cards are non-refundable once purchased, as per the <TextLink weight="regular" to="/giftcards-terms" target="_blank">Terms and Conditions</TextLink>.
        </BodyText>
        {showExpirationMessage && <BodyText variant="large">
          Your recipient must book by {moment(experienceView.originalExperience?.bookByDate ?? experienceView?.originalExperience?.expirationDate).format(DMY_CASUAL_FORMAT)}{' '}
          for a date up to {moment(experienceView?.originalExperience?.expirationDate).format(DMY_CASUAL_FORMAT)}.*
        </BodyText>}
        {!hasExpirationDates && <BodyText variant="large">Your recipient has three years to use their Gift Card*</BodyText>}
        <Caption variant="medium">
          *Subject to availability and seasonal surcharges. Redeem soon to secure this offer or use the voucher for a similar experience of equal value.
        </Caption>
      </VerticalSpacer>}
      <ExperienceOfferPageTicketSelection
        isGift
        experienceView={experienceView}
      />
    </Modal>
  )
}

export default ExperienceGiveAsGiftModal
